import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Button,
    VStack,
    HStack,
    FormControl,
    FormLabel,
    FormErrorMessage,
    RadioGroup,
    Radio,
    useToast,
    Box,
    Text,
    Textarea,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useCancelReturnParts } from '../hooks/useCancelReturnParts';
import PartBayButton from '../components/PartBayButton';
import { AutomationTarget } from '../hooks/useGetOrderWorkflowStatus';

export interface CancelReturnPartSummary {
    partId: string;
    name: string;
    partNumber?: string | null;
}

interface CancelReturnPartsModalProps {
    isOpen: boolean;
    onClose: () => void;
    supplierOrderId: string;
    selectedPartIds: string[];
    onSuccess: () => void;
    source?: AutomationTarget | 'inventory';
    /** When set (e.g. from an Actions menu), preselect Cancel vs Return. */
    initialAction?: 'return' | 'cancellation';
    /**
     * Display info for the parts in scope. Cancel/return is destructive and
     * irreversible in-app, so when the caller can fall back to "every eligible
     * part on the supplier order" (no explicit selection) the modal must show
     * exactly what will be affected.
     */
    parts?: CancelReturnPartSummary[];
    cancelOnly?: boolean;
}

interface ReasonOption {
    // Unique value for the radio group (the partstrader `id` is not unique on its own).
    key: string;
    // Reason id sent to the source's cancel/return API (partstrader/CCC use their ids,
    // OpsTrax uses its returns reasonId 1-11).
    id: number;
    // Label shown in the form.
    label: string;
    // Fragment used to build the human-readable CCC message, designed to read
    // naturally after "because ...". Not used for OpsTrax.
    messageReason?: string;
    // When true this reason requires a free-text note (e.g. "Other").
    addNotes?: boolean;
}

const RETURN_REASONS: Array<ReasonOption> = [
    { key: 'return-duplicate', id: 101, label: 'Duplicate of another part', messageReason: 'it is a duplicate of another part' },
    { key: 'return-invalid', id: 102, label: 'Not a Valid Part', messageReason: 'it is not a valid part' },
    { key: 'return-held-in-stock', id: 103, label: 'Part Held in Stock', messageReason: 'the part is being held in stock' },
    { key: 'return-repaired', id: 104, label: 'Part Repaired/reconditioned instead of replaced', messageReason: 'the part was repaired/reconditioned instead of replaced' },
    { key: 'return-wrong-info', id: 105, label: 'Wrong/Insufficient Information entered', messageReason: 'the wrong or insufficient information was entered' },
    { key: 'return-poor-fit', id: 105, label: 'Poor fit', messageReason: 'of poor fit' },
    { key: 'return-wrong-part', id: 106, label: 'Delivered wrong part', messageReason: 'the wrong part was delivered' },
    { key: 'return-damaged', id: 107, label: 'Received damaged part', messageReason: 'we received a damaged part' },
    { key: 'return-other', id: 1, label: 'Other', addNotes: true },
];

const CANCELLATION_REASONS: Array<ReasonOption> = [
    { key: 'cancel-changed-mind', id: 201, label: 'Customer changed mind', messageReason: 'the customer changed their mind' },
    { key: 'cancel-not-required', id: 202, label: 'Parts no longer required', messageReason: 'the parts are no longer required' },
    { key: 'cancel-supplier-cannot-provide', id: 203, label: 'Supplier cannot provide the part(s)', messageReason: 'the supplier cannot provide the part(s)' },
    { key: 'cancel-delivery-time', id: 204, label: 'Supplier changed the delivery time', messageReason: 'the supplier changed the delivery time' },
    { key: 'cancel-price', id: 205, label: 'Supplier changed the price', messageReason: 'the supplier changed the price' },
    { key: 'cancel-other', id: 1, label: 'Other', addNotes: true },
];

const CCC_RETURN_REASONS: Array<ReasonOption> = [
    { key: 'ccc-return-duplicate', id: 101, label: 'Duplicate of another part', messageReason: 'it is a duplicate of another part' },
    { key: 'ccc-return-invalid', id: 102, label: 'Not a Valid Part', messageReason: 'it is not a valid part' },
    { key: 'ccc-return-held-in-stock', id: 103, label: 'Part Held in Stock', messageReason: 'the part is being held in stock' },
    { key: 'ccc-return-repaired', id: 104, label: 'Part Repaired/reconditioned instead of replaced', messageReason: 'the part was repaired/reconditioned instead of replaced' },
    { key: 'ccc-return-wrong-info', id: 105, label: 'Wrong/Insufficient Information entered', messageReason: 'the wrong or insufficient information was entered' },
    { key: 'ccc-return-poor-fit', id: 105, label: 'Poor fit', messageReason: 'of poor fit' },
    { key: 'ccc-return-wrong-part', id: 106, label: 'Delivered wrong part', messageReason: 'the wrong part was delivered' },
    { key: 'ccc-return-damaged', id: 107, label: 'Received damaged part', messageReason: 'we received a damaged part' },
    { key: 'ccc-return-other', id: 108, label: 'Other', addNotes: true },
];
const CCC_CANCELLATION_REASONS: Array<ReasonOption> = [
    { key: 'ccc-cancel-changed-mind', id: 201, label: 'Customer changed mind', messageReason: 'the customer changed their mind' },
    { key: 'ccc-cancel-not-required', id: 202, label: 'Parts no longer required', messageReason: 'the parts are no longer required' },
    { key: 'ccc-cancel-supplier-cannot-provide', id: 203, label: 'Supplier cannot provide the part(s)', messageReason: 'the supplier cannot provide the part(s)' },
    { key: 'ccc-cancel-delivery-time', id: 204, label: 'Supplier changed the delivery time', messageReason: 'the supplier changed the delivery time' },
    { key: 'ccc-cancel-price', id: 205, label: 'Supplier changed the price', messageReason: 'the supplier changed the price' },
    { key: 'ccc-cancel-other', id: 206, label: 'Other', addNotes: true },
];

// OpsTrax return/cancel reasons (mirrors GET /api/shop/returns/reasons, ids 1-11).
// OpsTrax uses a single reason list regardless of return vs cancellation. "Other"
// (id 3) requires a free-text note (addNotes).
const OPSTRAX_REASONS: Array<ReasonOption> = [
    { key: 'ops-1', id: 1, label: 'Damaged Part' },
    { key: 'ops-2', id: 2, label: 'Wrong Part' },
    { key: 'ops-4', id: 4, label: 'Not Needed' },
    { key: 'ops-5', id: 5, label: 'Job Totaled' },
    { key: 'ops-6', id: 6, label: 'Core Return' },
    { key: 'ops-7', id: 7, label: 'Not Ordered' },
    { key: 'ops-8', id: 8, label: 'Invoiced Incorrect' },
    { key: 'ops-9', id: 9, label: 'Poor Fit' },
    { key: 'ops-10', id: 10, label: 'Repair Cancelled' },
    { key: 'ops-11', id: 11, label: 'Vendor Sent Incorrect Part' },
    { key: 'ops-3', id: 3, label: 'Other', addNotes: true },
];

const COLLISIONLINK_REASONS: Array<ReasonOption> = [
    { key: 'cl-not-needed', id: 0, label: 'Part Not Needed' },
    { key: 'cl-wrong-part', id: 1, label: 'Customer Requested Wrong Part' },
    { key: 'cl-damaged', id: 2, label: 'Damaged Part' },
    { key: 'cl-dealer-wrong', id: 3, label: 'Dealer Sent Wrong Part' },
    { key: 'cl-repair-cancelled', id: 4, label: 'Repair Cancelled/Totaled' },
];

const DEFAULT_RETURN_REASON_KEY = RETURN_REASONS[0].key;
const DEFAULT_CANCELLATION_REASON_KEY = CANCELLATION_REASONS[0].key;
const DEFAULT_CCC_RETURN_REASON_KEY = CCC_RETURN_REASONS[0].key;
const DEFAULT_CCC_CANCELLATION_REASON_KEY = CCC_CANCELLATION_REASONS[0].key;
const DEFAULT_OPSTRAX_REASON_KEY = OPSTRAX_REASONS[0].key;
const DEFAULT_COLLISIONLINK_REASON_KEY = COLLISIONLINK_REASONS[0].key;

// The fixed "Hi, we're ... because" lead-in shown as fill-in-the-blank in the UI
// and reused when building the supplier message so the two always stay in sync.
const buildSupplierMessagePrefix = (
    action: 'return' | 'cancellation',
    partCount: number,
): string => {
    const partWord = partCount > 1 ? 'these parts' : 'this part';
    const verb = action === 'return' ? 'returning' : 'cancelling';
    return `Hi, we're ${verb} ${partWord} because`;
};

// Builds the human-readable sentence sent to the supplier (CCC chat message /
// OpsTrax reason note) based on the form selections. Re-order intent is
// intentionally omitted from the message.
const buildSupplierMessage = (
    action: 'return' | 'cancellation',
    reasonText: string,
    partCount: number,
): string => {
    return `${buildSupplierMessagePrefix(action, partCount)} ${reasonText}.`;
};

const CancelReturnPartsModal = ({
    isOpen,
    onClose,
    supplierOrderId,
    selectedPartIds,
    onSuccess,
    source,
    initialAction = 'cancellation',
    parts,
    cancelOnly = false,
}: CancelReturnPartsModalProps) => {
    const [action, setAction] = useState<'return' | 'cancellation'>(initialAction);
    const [reasonKey, setReasonKey] = useState<string>(
        initialAction === 'return' ? DEFAULT_RETURN_REASON_KEY : DEFAULT_CANCELLATION_REASON_KEY
    );
    const [reOrder, setReOrder] = useState(true);
    const [note, setNote] = useState('');
    const toast = useToast();
    const cancelReturnMutation = useCancelReturnParts();

    const isCcc = source === 'ccc';
    const isOpsTrax = source === 'opstrax';
    const isCollisionLink = source === 'collisionlink';
    const isPartsTrader = source === 'partstrader';
    // Each marketplace uses its own reason list. OpsTrax and CollisionLink share
    // one list for both return and cancel. RETURN_REASONS / CANCELLATION_REASONS
    // are PartsTrader (including Other id 1); Tesla / Mitchell / inventory use
    // those same presets without Other.
    let currentReasons: ReasonOption[];
    if (isOpsTrax) {
        currentReasons = OPSTRAX_REASONS;
    } else if (isCollisionLink) {
        currentReasons = COLLISIONLINK_REASONS;
    } else if (isCcc) {
        currentReasons = action === 'return' ? CCC_RETURN_REASONS : CCC_CANCELLATION_REASONS;
    } else if (isPartsTrader) {
        currentReasons = action === 'return' ? RETURN_REASONS : CANCELLATION_REASONS;
    } else {
        currentReasons = (action === 'return' ? RETURN_REASONS : CANCELLATION_REASONS)
            .filter((reason) => !reason.addNotes);
    }
    const selectedReason = currentReasons.find((reason) => reason.key === reasonKey) ?? currentReasons[0];
    const otherReason = currentReasons.find((reason) => reason.addNotes);
    // CCC / OpsTrax keep the note field visible. PartsTrader only shows it after Other.
    const showNoteField = isPartsTrader ? !!selectedReason.addNotes : !!otherReason;
    const noteRequired = !!selectedReason.addNotes;
    // When "Other" is selected the note must be non-empty so we never send an
    // empty comment to CCC/OpsTrax and can build a meaningful supplier message.
    const isNoteMissing = noteRequired && !note.trim();
    // Free-text notes (CCC/OpsTrax "Other") are wrapped in the same sentence as
    // preset reasons, so we show the lead-in as a fill-in-the-blank prompt.
    const supplierMessagePrefix = buildSupplierMessagePrefix(action, selectedPartIds.length);

    // Reset form state (and pick the right default reason for the source) on open.
    useEffect(() => {
        if (!isOpen) return;
        setAction(initialAction);
        if (isOpsTrax) {
            setReasonKey(DEFAULT_OPSTRAX_REASON_KEY);
        } else if (isCollisionLink) {
            setReasonKey(DEFAULT_COLLISIONLINK_REASON_KEY);
        } else if (isCcc) {
            setReasonKey(initialAction === 'return' ? DEFAULT_CCC_RETURN_REASON_KEY : DEFAULT_CCC_CANCELLATION_REASON_KEY);
        } else {
            setReasonKey(initialAction === 'return' ? DEFAULT_RETURN_REASON_KEY : DEFAULT_CANCELLATION_REASON_KEY);
        }
        setReOrder(true);
        setNote('');
    }, [isOpen, isOpsTrax, isCollisionLink, isCcc, initialAction]);

    const handleSubmit = async () => {
        if (noteRequired && !note.trim()) {
            toast({
                title: 'Please add a note for this reason.',
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
            return;
        }
        try {
            // CCC has no dedicated cancel/return API, so the supplier is notified via a
            // chat message: canned sentence for preset reasons, or the raw user note for
            // "Other". OpsTrax uses its returns API (reasonId carries the intent);
            // `comment` is sent as the optional reasonNote. CollisionLink always sends
            // reasonId 0 and the selected reason label as comment.
            let comment: string | undefined;
            let reasonId = selectedReason.id;
            if (isOpsTrax) {
                // OpsTrax carries intent via reasonId; a note is only present for
                // "Other". Wrap it in the same sentence as CCC so the free-text
                // reason note reads naturally to the supplier.
                const trimmedNote = note.trim();
                comment = trimmedNote
                    ? buildSupplierMessage(action, trimmedNote, selectedPartIds.length)
                    : undefined;
            } else if (isCollisionLink) {
                reasonId = 0;
                comment = selectedReason.label;
            } else if (isCcc) {
                // For "Other" the user's typed note becomes the reason fragment so
                // the message reads naturally after "because ...", matching the
                // canned preset reasons.
                const reasonText = selectedReason.addNotes ? note.trim() : selectedReason.messageReason ?? '';
                comment = buildSupplierMessage(action, reasonText, selectedPartIds.length);
            } else if (isPartsTrader && selectedReason.addNotes) {
                comment = note.trim();
            }

            const trimmedNote = note.trim().replace(/\s+/g, ' ');
            const reasonLabel = selectedReason.addNotes && trimmedNote
                ? `${selectedReason.label}: ${trimmedNote}`
                : selectedReason.label;

            await cancelReturnMutation.mutateAsync({
                supplierOrderId,
                partIds: selectedPartIds,
                reasonId,
                action,
                reOrder,
                reasonLabel,
                ...(comment ? { comment } : {}),
            });

            const successTitle = isOpsTrax || isCollisionLink
                ? `${action === 'return' ? 'Return' : 'Cancellation'} submitted to the supplier. Please check back shortly.`
                : isCcc
                    ? `Message sent to the supplier to ${action === 'return' ? 'return' : 'cancel'} the part(s).`
                    : action === 'return' ? 'Parts marked for return. Please check back shortly' : 'Parts marked for cancellation. Please check back shortly';
            toast({
                title: successTitle,
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            onSuccess();
            handleClose();
        } catch (error) {
            toast({
                title: `Failed to ${action === 'return' ? 'return' : 'cancel'} parts`,
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleClose = () => {
        setAction('cancellation');
        setReasonKey(
            isOpsTrax
                ? DEFAULT_OPSTRAX_REASON_KEY
                : isCollisionLink
                    ? DEFAULT_COLLISIONLINK_REASON_KEY
                    : isCcc
                        ? DEFAULT_CCC_CANCELLATION_REASON_KEY
                        : DEFAULT_CANCELLATION_REASON_KEY
        );
        setReOrder(true);
        setNote('');
        onClose();
    };

    const handleActionChange = (newAction: string) => {
        setAction(newAction as 'return' | 'cancellation');
        // OpsTrax and CollisionLink use a single reason list for both actions;
        // PartsTrader and CCC swap to their own return/cancel set.
        if (isOpsTrax || isCollisionLink) return;
        if (isCcc) {
            setReasonKey(newAction === 'return' ? DEFAULT_CCC_RETURN_REASON_KEY : DEFAULT_CCC_CANCELLATION_REASON_KEY);
            return;
        }
        setReasonKey(newAction === 'return' ? DEFAULT_RETURN_REASON_KEY : DEFAULT_CANCELLATION_REASON_KEY);
    };

    const isSubmitting = cancelReturnMutation.isPending;

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="md" scrollBehavior="outside" isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader bg="gray.50" borderTopRadius="md" borderBottomWidth="1px" borderColor="gray.200">
                    {action === 'return' ? 'Return Parts' : 'Cancel Parts'}
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack spacing={4} align="stretch">
                        {/* Affected parts — always spell out the scope of a destructive action */}
                        {parts && parts.length > 0 && (
                            <Box
                                p={4}
                                border="1px solid"
                                borderColor="gray.200"
                                borderRadius="lg"
                            >
                                <FormLabel fontWeight="semibold" fontSize="md" mb={2}>
                                    {action === 'return' ? 'Parts to Return' : 'Parts to Cancel'} ({parts.length})
                                </FormLabel>
                                <VStack align="stretch" spacing={1} maxH="140px" overflowY="auto">
                                    {parts.map((part) => (
                                        <Text key={part.partId} fontSize="sm" color="gray.700" noOfLines={1}>
                                            • {part.name}
                                            {part.partNumber ? (
                                                <Text as="span" color="gray.500"> — {part.partNumber}</Text>
                                            ) : null}
                                        </Text>
                                    ))}
                                </VStack>
                            </Box>
                        )}

                        {/* Type of Action Section */}
                        <Box
                            p={4}
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="lg"
                        >
                            <FormControl>
                                <FormLabel fontWeight="semibold" fontSize="md" mb={3}>Type of Action</FormLabel>
                                <RadioGroup value={action} onChange={handleActionChange}>
                                    <VStack align="start" spacing={3}>
                                        {!cancelOnly && (
                                            <Radio value="return" colorScheme="blue">
                                                Return
                                            </Radio>
                                        )}
                                        <Radio value="cancellation" colorScheme="blue">
                                            Cancellation
                                        </Radio>
                                    </VStack>
                                </RadioGroup>
                            </FormControl>
                        </Box>

                        {/* Reason Section */}
                        <Box
                            p={4}
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="lg"
                        >
                            <FormControl>
                                <FormLabel fontWeight="semibold" fontSize="md" mb={3}>Reason</FormLabel>
                                <RadioGroup value={reasonKey} onChange={(value) => setReasonKey(value)}>
                                    <VStack align="start" spacing={3}>
                                        {currentReasons.map((reason) => (
                                            <Radio key={reason.key} value={reason.key} colorScheme="blue">
                                                {reason.label}
                                            </Radio>
                                        ))}
                                    </VStack>
                                </RadioGroup>
                                {showNoteField && (
                                    <FormControl isRequired={noteRequired} isInvalid={isNoteMissing} mt={3}>
                                        {isPartsTrader ? (
                                            <FormLabel fontSize="sm" mb={1}>Comment</FormLabel>
                                        ) : (
                                            <Text fontSize="sm" color="gray.600" mb={1}>
                                                {supplierMessagePrefix}{' '}
                                                <Text as="span" fontWeight="semibold">
                                                    {note.trim() || '_______'}
                                                </Text>
                                                .
                                            </Text>
                                        )}
                                        <Textarea
                                            size="sm"
                                            minH="40px"
                                            placeholder={isPartsTrader ? 'Add a comment' : 'e.g. the part arrived damaged'}
                                            value={note}
                                            onChange={(e) => setNote(e.target.value)}
                                            onFocus={() => {
                                                if (otherReason) setReasonKey(otherReason.key);
                                            }}
                                        />
                                        {isNoteMissing && (
                                            <FormErrorMessage>
                                                {isPartsTrader ? 'Please enter a comment.' : 'Please enter a reason.'}
                                            </FormErrorMessage>
                                        )}
                                    </FormControl>
                                )}
                            </FormControl>
                        </Box>

                        {/* Re-Order Parts Section */}
                        <Box
                            p={4}
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="lg"
                        >
                            <FormControl>
                                <FormLabel fontWeight="semibold" fontSize="md" mb={3}>Re-Order Parts</FormLabel>
                                <RadioGroup value={reOrder ? 'yes' : 'no'} onChange={(value) => setReOrder(value === 'yes')}>
                                    <VStack align="start" spacing={3}>
                                        <Radio value="yes" colorScheme="blue">
                                            Yes
                                        </Radio>
                                        <Radio value="no" colorScheme="blue">
                                            No
                                        </Radio>
                                    </VStack>
                                </RadioGroup>
                            </FormControl>
                        </Box>
                    </VStack>
                </ModalBody>

                <ModalFooter>
                    <HStack spacing={3}>
                        <Button
                            size="action"
                            variant="outlineGray"
                            onClick={handleClose}
                            isDisabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <PartBayButton
                            size="action"
                            colorScheme="partbay-blue"
                            variant="solid"
                            onClick={handleSubmit}
                            isLoading={isSubmitting}
                            loadingText="Processing..."
                            isDisabled={selectedPartIds.length === 0 || isNoteMissing}
                            text="Confirm"
                        />
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default CancelReturnPartsModal;

